import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import {
  AnalyticsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DashboardIcon,
  // TODO: Re-enable these icons when ready
  // HelpIcon,
  LiveViewIcon,
  NotificationsIcon,
  PlaybackIcon,
  SettingsIcon,
} from "@/shared/ui/console-icons";
import { createDeploymentRoutePath } from "@/features/deployment-selection/utils/deployment-paths";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import {
  canViewDashboard,
  canViewLiveView,
  canViewAnalytics,
  canViewNotifications,
  canViewPlayback,
  // TODO: Re-enable these permissions when ready
  canViewSettings,
} from "@/features/deployment-selection/model/permissions";
import { SidebarDeploymentSwitcher } from "@/features/deployment-selection/ui/sidebar-deployment-switcher";
import { useNotificationCenter } from "@/features/deployment-dashboard/notification-center";
import { SidebarNavItem } from "@/shared/ui/sidebar-nav-item";
import { UserSessionMenu } from "@/shared/ui/user-session-menu";

export const SIDEBAR_EXPANDED_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 56;

interface NavEntry {
  to: string;
  icon: ReactNode;
  label: string;
  end?: boolean;
  badge?: string;
  badgeColor?: "default" | "success";
  visible?: boolean; // Whether the nav item should be shown based on permissions
}

function createPrimaryNav(
  deploymentId: string,
  deployment: ReturnType<typeof useSelectedDeployment>["selectedDeployment"],
  notificationCount: number
): NavEntry[] {
  return [
    {
      to: createDeploymentRoutePath(deploymentId, "dashboard"),
      end: true,
      icon: <DashboardIcon />,
      label: "Dashboard",
      visible: deployment ? canViewDashboard(deployment) : false,
    },
    {
      to: createDeploymentRoutePath(deploymentId, "live"),
      icon: <LiveViewIcon />,
      label: "Live view",
      badge: "LIVE",
      badgeColor: "success",
      visible: deployment ? canViewLiveView(deployment) : false,
    },
    {
      to: createDeploymentRoutePath(deploymentId, "analytics"),
      icon: <AnalyticsIcon />,
      label: "Analytics",
      visible: deployment ? canViewAnalytics(deployment) : false,
    },
    {
      to: createDeploymentRoutePath(deploymentId, "notifications"),
      icon: <NotificationsIcon />,
      label: "Notifications",
      badge: notificationCount > 0 ? notificationCount.toString() : undefined,
      visible: deployment ? canViewNotifications(deployment) : false,
    },
    {
      to: createDeploymentRoutePath(deploymentId, "playback"),
      icon: <PlaybackIcon />,
      label: "Historical Playback",
      visible: deployment ? canViewPlayback(deployment) : false,
    },
  ];
}

function createSecondaryNav(
  deploymentId: string,
  deployment: ReturnType<typeof useSelectedDeployment>["selectedDeployment"]
): NavEntry[] {
  return [
    {
      to: createDeploymentRoutePath(deploymentId, "settings"),
      icon: <SettingsIcon />,
      label: "Settings",
      visible: deployment ? canViewSettings(deployment) : false,
    },
    // TODO: Re-enable help navigation when ready
    // {
    //   to: createDeploymentRoutePath(deploymentId, "help"),
    //   icon: <HelpIcon />,
    //   label: "Help",
    //   visible: true, // Help is always visible
    // },
  ];
}

// ----- Private internal components -----

interface SidebarLogoProps {
  collapsed: boolean;
}

function SidebarLogo({ collapsed }: SidebarLogoProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: collapsed ? 0 : 2,
        py: 1.5,
        justifyContent: collapsed ? "center" : "flex-start",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src="/icon.png"
        alt=""
        aria-hidden="true"
        sx={{ width: 28, height: 28, flexShrink: 0 }}
      />
      {!collapsed && (
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1rem",
            letterSpacing: "-0.03em",
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          situate
        </Typography>
      )}
    </Box>
  );
}

interface NavListProps {
  items: NavEntry[];
  collapsed: boolean;
}

function NavList({ items, collapsed }: NavListProps) {
  // Filter out items that should not be visible based on permissions
  const visibleItems = items.filter((item) => item.visible !== false);

  return (
    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, flexShrink: 0 }}>
      {visibleItems.map((item) => (
        <Box component="li" key={item.to}>
          <SidebarNavItem
            to={item.to}
            end={item.end}
            icon={item.icon}
            label={item.label}
            badge={item.badge}
            badgeColor={item.badgeColor}
            collapsed={collapsed}
          />
        </Box>
      ))}
    </Box>
  );
}

interface CollapseToggleProps {
  collapsed: boolean;
  onToggle: () => void;
}

function CollapseToggle({ collapsed, onToggle }: CollapseToggleProps) {
  return (
    <Box
      sx={(theme) => ({
        borderTop: `1px solid ${alpha(theme.palette.common.white, 0.07)}`,
        px: collapsed ? 0 : 1.5,
        py: 0.75,
        display: "flex",
        justifyContent: collapsed ? "center" : "flex-start",
      })}
    >
      <Tooltip title={collapsed ? "Expand sidebar" : ""} placement="right" arrow>
        <ButtonBase
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: collapsed ? 0.75 : 1,
            py: 0.625,
            borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.05),
              color: "text.primary",
            },
          })}
        >
          {collapsed ? (
            <ChevronRightIcon sx={{ fontSize: "1rem" }} />
          ) : (
            <>
              <ChevronLeftIcon sx={{ fontSize: "1rem" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "inherit", whiteSpace: "nowrap" }}
              >
                Collapse
              </Typography>
            </>
          )}
        </ButtonBase>
      </Tooltip>
    </Box>
  );
}

// ----- Public component -----

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  const { deploymentId, selectedDeployment } = useSelectedDeployment();
  const { unresolvedAlertCount } = useNotificationCenter();
  const primaryNav = createPrimaryNav(deploymentId, selectedDeployment, unresolvedAlertCount);
  const secondaryNav = createSecondaryNav(deploymentId, selectedDeployment);

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      data-collapsed={collapsed}
      sx={(theme) => ({
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${alpha(theme.palette.common.white, 0.07)}`,
        flexShrink: 0,
        overflowX: "hidden",
        overflowY: "auto",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      })}
    >
      <SidebarLogo collapsed={collapsed} />
      <SidebarDeploymentSwitcher collapsed={collapsed} />
      <Divider sx={{ mx: collapsed ? 0.5 : 1, my: 0.5 }} />
      <NavList items={primaryNav} collapsed={collapsed} />
      <Divider sx={{ mx: collapsed ? 0.5 : 1, my: 0.5 }} />
      <NavList items={secondaryNav} collapsed={collapsed} />
      <Box sx={{ flexGrow: 1 }} />
      <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} />
      <UserSessionMenu collapsed={collapsed} />
    </Box>
  );
}
